import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaSearch } from 'react-icons/fa';
import { teacherAPI, getImageUrl } from '../services/api';
import { formatApiError } from '../utils/errorUtils';
import Modal from '../components/Modal';
import './TeachersPage.css';

function TeachersPage() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    FirstName: '',
    LastName: '',
    ContactNumber: '',
    Specialization: '',
    PhotoPath: '',
    IsAdmin: false,
  });

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const response = await teacherAPI.getAll();
      setTeachers(response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch teachers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredTeachers = teachers.filter((teacher) => {
    const fullName = `${teacher.FirstName || ''} ${teacher.LastName || ''}`;
    return fullName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleOpenModal = (teacher = null) => {
    if (teacher) {
      setEditingId(teacher.TeacherID);
      setFormData({
        FirstName: teacher.FirstName || '',
        LastName: teacher.LastName || '',
        ContactNumber: teacher.ContactNumber || '',
        Specialization: teacher.Specialization || '',
        PhotoPath: teacher.PhotoPath || '',
        IsAdmin: true,
      });
    } else {
      setEditingId(null);
      setFormData({
        FirstName: '',
        LastName: '',
        ContactNumber: '',
        Specialization: '',
        PhotoPath: '',
        IsAdmin: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    if (type === 'file' && files.length > 0) {
      // Store the file object for FormData
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Create FormData for multipart/form-data submission
      const data = new FormData();
      
      // Add all form fields to FormData
      Object.keys(formData).forEach((key) => {
        if (key === 'PhotoPath') {
          // Only add the file if it's a File object (new upload)
          if (formData[key] instanceof File) {
            data.append(key, formData[key]);
          }
        } else {
          data.append(key, key === 'IsAdmin' ? true : formData[key]);
        }
      });

      if (editingId) {
        await teacherAPI.update(editingId, data);
      } else {
        await teacherAPI.create(data);
      }
      await fetchTeachers();
      handleCloseModal();
      setError('');
    } catch (err) {
      setError('Failed to save teacher: ' + formatApiError(err, 'Unable to save teacher'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this teacher?')) {
      try {
        await teacherAPI.delete(id);
        await fetchTeachers();
        setError('');
      } catch (err) {
        setError('Failed to delete teacher: ' + formatApiError(err, 'Unable to delete teacher'));
      }
    }
  };

  const teacherStats = [
    { label: 'Total Teachers', value: teachers.length },
    { label: 'Teachers', value: teachers.filter((item) => item.IsAdmin === true || item.IsAdmin === 1).length },
    { label: 'With Contact', value: teachers.filter((item) => item.ContactNumber).length },
    { label: 'With Specialization', value: teachers.filter((item) => item.Specialization).length }
  ];

  return (
    <div className="teachers-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Teacher Management</h1>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <FaPlus /> Add Teacher
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="summary-grid">
          {teacherStats.map((item) => (
            <article className="summary-card" key={item.label}>
              <p>{item.label}</p>
              <h3>{item.value}</h3>
            </article>
          ))}
        </div>

        <div className="search-box">
          <FaSearch />
            <input
              type="text"
              placeholder="Search teachers by name..."
              value={searchTerm}
              onChange={handleSearch}
          />
        </div>

        {loading ? (
          <div className="loading">Loading teachers...</div>
        ) : filteredTeachers.length === 0 ? (
          <div className="no-data">No teachers found</div>
        ) : (
          <div className="table-responsive">
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Contact</th>
                  <th>Specialization</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeachers.map((teacher) => (
                  <tr key={teacher.TeacherID}>
                    <td data-label="Name">{teacher.FirstName} {teacher.LastName}</td>
                    <td data-label="Role">Teacher</td>
                    <td data-label="Contact">{teacher.ContactNumber || 'N/A'}</td>
                    <td data-label="Specialization">{teacher.Specialization || 'N/A'}</td>
                    <td data-label="Actions" className="action-buttons">
                      <button
                        className="btn btn-small btn-primary"
                        onClick={() => handleOpenModal(teacher)}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => handleDelete(teacher.TeacherID)}
                      >
                        <FaTrash /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
      title={editingId ? 'Edit Teacher' : 'Add New Teacher'}
        onClose={handleCloseModal}
      >
        <form onSubmit={handleSubmit} className="teacher-form">
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              name="FirstName"
              value={formData.FirstName}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Last Name *</label>
            <input
              type="text"
              name="LastName"
              value={formData.LastName}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Contact Number</label>
            <input
              type="tel"
              name="ContactNumber"
              value={formData.ContactNumber}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Teacher Photo</label>
            <input
              type="file"
              name="PhotoPath"
              onChange={handleInputChange}
              accept="image/*"
            />
            <p className="form-help">Use a square JPG or PNG image, ideally 800 x 800 px or larger, for the teacher avatar and dashboard card.</p>
            {formData.PhotoPath && (
              <div className="upload-preview">
                <img 
                  src={formData.PhotoPath instanceof File ? URL.createObjectURL(formData.PhotoPath) : getImageUrl(formData.PhotoPath)} 
                  alt="Teacher" 
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Specialization</label>
            <input
              type="text"
              name="Specialization"
              value={formData.Specialization}
              onChange={handleInputChange}
              placeholder="e.g., Management, Operations"
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update Teacher' : 'Add Teacher'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseModal}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default TeachersPage;
